import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { mediaQueryLarge, scheduler, viewTransition } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#cancelSelection, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();
  #selection = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#cancelSelection();
  }

  #handleVariantUpdate = (event) => {
    const { resource, data } = event.detail;
    const mediaId = resource?.featured_media?.id;
    const productChanged = data?.newProduct && String(data.productId) !== this.dataset.productId;

    if (this.dataset.productId && data?.productId && String(data.productId) !== this.dataset.productId && !productChanged) {
      return;
    }

    this.#cancelSelection();
    if (!mediaId && !productChanged) return;

    if (productChanged || !this.querySelector(`[slide-id="${mediaId}"], [data-media-id="${mediaId}"]`)) {
      const source = data?.html?.querySelector('media-gallery');
      if (!source) return;
      const replacement = source.cloneNode(true);
      this.replaceWith(replacement);
      if (replacement instanceof MediaGallery && mediaId) replacement.#selectVariantMedia(String(mediaId));
      return;
    }

    this.#selectVariantMedia(String(mediaId));
  };

  #cancelSelection = () => {
    this.#selection++;
  };

  async #selectVariantMedia(mediaId) {
    const selection = ++this.#selection;
    const isCurrent = () => this.isConnected && selection === this.#selection;

    await customElements.whenDefined('slideshow-component');
    if (viewTransition.current) await viewTransition.current.catch(() => {});
    if (!isCurrent()) return;

    scheduler.schedule(async () => {
      await new Promise(requestAnimationFrame);
      if (!isCurrent()) return;

      const gridItem = this.querySelector(`.media-gallery__grid [data-media-id="${mediaId}"]`);
      if (this.presentation === 'grid' && mediaQueryLarge.matches && gridItem) {
        gridItem.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
        return;
      }

      const quickAddSlide = this.closest('.quick-add-modal__content .product-information__media')
        ? this.querySelector(`slideshow-slide[slide-id="${mediaId}"]`)
        : null;
      if (mediaQueryLarge.matches && quickAddSlide) {
        quickAddSlide.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
        return;
      }

      const slideshow = this.slideshow;
      if (!slideshow) return;
      while (slideshow.hasAttribute('transitioning')) {
        await new Promise(requestAnimationFrame);
        if (!isCurrent()) return;
      }
      slideshow.select({ id: mediaId }, undefined, { animate: false });
    });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
